import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core';
import { FormattedMessage } from 'react-intl';

import { Modal } from '../../molecules/Modal';
import Button from '../../button';
import { BasicTableCell } from '../../molecules/Table/BasicTableCell';
import { Pagination } from '../../molecules/Table/Pagination';
import { useDownloadExportRequest, useGetExportRequests } from '../../../queries/queryExportRequests';
import { parseStringDateToFormat } from '../../../utils/utils';
import { STATUS_LABEL, canDownload, readExportRequests } from './exportRequestUtils';
import styles from './DownloadCenterHistoryModal.styles';

const useStyles = makeStyles(styles);

const DEFAULT_PAGE_SIZE = 25;

export const DownloadCenterHistoryModal = ({ open, onClose, clientId }) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useGetExportRequests({ clientId });

  const exportRequests = readExportRequests(data);
  const totalCount = exportRequests.length;
  const visibleExportRequests = exportRequests.slice(page * pageSize, page * pageSize + pageSize);

  const downloadExportRequest = useDownloadExportRequest();

  const handleDownload = exportRequest =>
    downloadExportRequest.mutate({ clientId, exportRequestId: exportRequest.id, filename: exportRequest.filename });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<FormattedMessage id="downloadCenter.history.title" defaultMessage="Historial de descargas" />}
    >
      <TableContainer>
        {isLoading ? (
          <Box className={classes.loader}>
            <CircularProgress />
          </Box>
        ) : exportRequests.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              <FormattedMessage id="downloadCenter.empty" defaultMessage="Aún no tienes archivos generados." />
            </Typography>
          </Box>
        ) : (
          <>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.tableRowContainer}>
                  <BasicTableCell className={classes.fileColumn}>
                    <Typography className={classes.tableHeader} variant="body2">
                      <FormattedMessage id="downloadCenter.history.column.file" defaultMessage="Archivo" />
                    </Typography>
                  </BasicTableCell>
                  <BasicTableCell className={classes.compactColumn}>
                    <Typography className={classes.tableHeader} variant="body2">
                      <FormattedMessage id="common.status" defaultMessage="Estado" />
                    </Typography>
                  </BasicTableCell>
                  <BasicTableCell className={classes.compactColumn}>
                    <Typography className={classes.tableHeader} variant="body2">
                      <FormattedMessage id="downloadCenter.history.column.requestedAt" defaultMessage="Solicitado" />
                    </Typography>
                  </BasicTableCell>
                  <BasicTableCell className={classes.compactColumn}>
                    <Typography className={classes.tableHeader} variant="body2">
                      <FormattedMessage id="downloadCenter.history.column.action" defaultMessage="Acción" />
                    </Typography>
                  </BasicTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleExportRequests.map(exportRequest => (
                  <TableRow key={exportRequest.id} className={classes.tableRowContainer}>
                    <BasicTableCell className={classes.fileColumn}>
                      <Typography variant="body2" className={classes.filename}>
                        {exportRequest.displayName}
                      </Typography>
                    </BasicTableCell>
                    <BasicTableCell className={classes.compactColumn}>
                      <Chip
                        size="small"
                        className={`${classes.statusChip} ${classes[`statusChip_${exportRequest.status}`] || ''}`}
                        label={STATUS_LABEL[exportRequest.status]}
                      />
                    </BasicTableCell>
                    <BasicTableCell className={classes.compactColumn}>
                      <Typography variant="body2">{parseStringDateToFormat(exportRequest.requestedAt)}</Typography>
                    </BasicTableCell>
                    <BasicTableCell className={classes.compactColumn}>
                      {canDownload(exportRequest) && (
                        <Button variant="outlined" size="small" onClick={() => handleDownload(exportRequest)}>
                          <FormattedMessage id="downloadCenter.download" defaultMessage="Descargar" />
                        </Button>
                      )}
                    </BasicTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              count={totalCount}
              rowsPerPage={[DEFAULT_PAGE_SIZE, 50, 100]}
              onChange={({ page: newPage, pageSize: newPageSize }) => {
                setPage(newPage);
                setPageSize(newPageSize);
              }}
            />
          </>
        )}
      </TableContainer>
    </Modal>
  );
};

DownloadCenterHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.number.isRequired
};
